'use strict'

import { ContentService } from "@/service/content.service";
import { SettingsService } from "@/service/settings.service";
import { CommonForm, CareGiver, Household, Child } from "@/models/common-form";
import { AppData, MARITAL_STATUS, INCOME_RANGE } from '@/models/app-data';
import { WORKFLOW_PREFERRED_HEAD_START, WORKFLOW_PREFERRED_HEAD_START_AND_GREAT_START, WORKFLOW_PREFERRED_GREAT_START } from '@/mixins/common-mixins';
import { FplSettings } from "@/models/fpl-settings";

const COMMON_FORM_PREFERRED_GREAT_START = "Great Start Readiness Program";
const COMMON_FORM_PREFERRED_HEAD_START = "Head Start or Early Head Start";
const COMMON_FORM_PREFERRED_GREAT_START_AND_HEAD_START = "Head Start or Early Head Start & Great Start Readiness Program";
const COMMON_FORM_PREFERRED_NONE = "None";

export class CommonFormBuilder {
    private contentService: ContentService;

    constructor() {
        this.contentService = new ContentService()
    }

    async BuildCommonForm(appData: AppData, captchaToken: string): Promise<CommonForm> {
        let commonForm = {} as CommonForm;
        const caregiver = {} as CareGiver;
        const household = {} as Household
        const children = [] as Array<Child>

        if (appData.careGiver != null) {
            caregiver.lastname = appData.careGiver.lastName;
            caregiver.firstname = appData.careGiver.firstName;
            caregiver.lastname = appData.careGiver.lastName;
            caregiver.firstname = appData.careGiver.firstName;
            caregiver.preferredname = appData.careGiver.preferredName;
            caregiver.phonenumber = appData.careGiver.phoneNumber;
            caregiver.email = appData.careGiver.email;
            caregiver.streetaddress = appData.careGiver.streetAddress;
            caregiver.city = appData.careGiver.city;
            caregiver.state = appData.careGiver.state;
            caregiver.latitude = appData.careGiver.latitude;
            caregiver.longitude = appData.careGiver.longitude;            

            // If address is not required, get the zip from the appData
            if ( !appData.careGiver.zipCode ) {
                caregiver.zipcode = Number(appData.zipCode);
                caregiver.county = appData.county;
            } else {
                caregiver.zipcode = Number(appData.careGiver.zipCode);
                caregiver.county = appData.careGiver.county;
            } 

            caregiver.currentlyhomeless = appData.careGiver.currentlyHomeless;
            caregiver.searchotherlocation = appData.careGiver.searchOtherLocation;
            caregiver.otheraddress = appData.careGiver.otherAddress;
            caregiver.othercity = appData.careGiver.otherCity;
            caregiver.otherstate = appData.careGiver.otherState;
            caregiver.othercounty = appData.careGiver.otherCounty;
            caregiver.otherlatitude = appData.careGiver.otherLatitude;
            caregiver.otherlongitude = appData.careGiver.otherLongitude;
            caregiver.otherzip = Number(appData.careGiver.otherZip);

            switch (appData.careGiver.maritalStatus) {
                case MARITAL_STATUS.DIVORCED:
                    caregiver.maritalstatus = "Divorced";
                    break;
                case MARITAL_STATUS.MARRIED:
                    caregiver.maritalstatus = "Married";
                    break;
                case MARITAL_STATUS.SINGLE:
                    caregiver.maritalstatus = "Single";
                    break;
            }

            caregiver.speakenglish = appData.careGiver.speakEnglish;
            caregiver.speakspanish = appData.careGiver.speakSpanish;
            caregiver.speakarabic = appData.careGiver.speakArabic;
            caregiver.speakother = appData.careGiver.speakOther;
            if (caregiver.speakother === true) {
                caregiver.otherlanguage = appData.careGiver.otherLanguage;
            }

            if (appData.household != null) {
                household.fulltime = appData.household.fullTime;
                household.parttime = appData.household.partTime;
                household.attendingschool = appData.household.attendingSchool;
                household.attendingschoolfulltime = appData.household.attendingSchoolFullTime;
                household.unemployed = appData.household.unemployed;
                household.retired = appData.household.retired;
                household.disabilitybenefits = appData.household.disabilityBenefits;
                household.unemploymentbenefits = appData.household.unemploymentBenefits;
                household.ssibenefits = appData.household.ssiBenefits;
                household.tanfbenefits = appData.household.tanfBenefits;
                household.mdhhs = appData.household.mdhhs;
                household.nobenefits = appData.household.noBenefits;
            }

            if (appData.children != null) {
                appData.children.forEach(function (childObj) {
                    const c: Child = {
                        fullname: childObj.fullName,
                        birthdate: childObj.birthdate,
                        specialneeds: childObj.specialNeeds,
                        hasspecialneedsplan: childObj.hasSpecialNeedsPlan
                    }

                    children.push(c);
                })
            }

            const settingsService = new SettingsService();
            const fplSettings = await settingsService.getFPLSettings();

            const incomeRange = this.IncomeRange(appData.eligibleIncome, appData.fpl, fplSettings)
            const incomepercentrange  = this.IncomeRangePercent(appData.fpl, fplSettings)
            const nonIncomeFactors = await this.NonIncomeFactors(appData)
            const howHeard = await this.getHowDidYouHear( appData)
            const preferredProgram = this.PreferredProgram(appData.preferredProgram);

            commonForm = {
                token: captchaToken,
                workflowid: appData.workflowId,
                caregiver: caregiver,
                household: household,
                children: children,
                additionalinformation: appData.additionalInfo,
                howheard: howHeard,
                zipcode: Number(appData.zipCode),
                numberinhousehold: appData.numberInHousehold,
                numberchildren3oryounger: appData.numberChildren - appData.number4YearOlds,
                numberchildren4: appData.number4YearOlds,
                incomerange: incomeRange,
                nonincomefactors: nonIncomeFactors,
                headstart: appData.applyHeadStart,
                greatstart: appData.applyGreatStart,
                signupready4k: appData.signupReady4k,
                followup211: appData.followUp211,
                preferredprogram: preferredProgram,
                language: appData.language,
                incomepercentrange: incomepercentrange,
            }
        }

        return commonForm
    }

    private async getHowDidYouHear( appData: AppData ): Promise<string> {

        let howDidYouHear = appData.howDidYouHear;

        if (appData.language === 'ar' || appData.language === 'es') {
            // Check for 'Other' selected
            if ( appData.howDidYouHearIndex === appData.howDidYouHearLastIndex)
            {
                howDidYouHear =  appData.howDidYouHearDetails;
            } else {
                const englishContentService = new ContentService();
                const englishContent = await englishContentService.getFinalDetailsContent();
                const englishChoices = englishContent.Choices;

                if (appData.howDidYouHearIndex < englishChoices.length) {
                    howDidYouHear =  englishChoices[appData.howDidYouHearIndex]
                }
            }
        }  else {
            // Check for 'Other' selected
            if ( appData.howDidYouHearIndex === appData.howDidYouHearLastIndex)
            {
                howDidYouHear = appData.howDidYouHearDetails;
            } 
        }

        return howDidYouHear;
    }

    private PreferredProgram(appDataPreferredProgram: string): string {
        let preferredProgram = COMMON_FORM_PREFERRED_NONE;

        if (appDataPreferredProgram === WORKFLOW_PREFERRED_GREAT_START) {
            preferredProgram = COMMON_FORM_PREFERRED_GREAT_START
        } else if (appDataPreferredProgram === WORKFLOW_PREFERRED_HEAD_START) {
            preferredProgram = COMMON_FORM_PREFERRED_HEAD_START
        } else if (appDataPreferredProgram === WORKFLOW_PREFERRED_HEAD_START_AND_GREAT_START) {
            preferredProgram = COMMON_FORM_PREFERRED_GREAT_START_AND_HEAD_START
        }
        return preferredProgram;
    }

    private IncomeRange(eligibleIncome: number, fpl: INCOME_RANGE, fplSettings: FplSettings): string {
        let incomeRange = '';
        const multiplier1 = fplSettings.FPL_LEVEL_1_MAX / 100;
        const multiplier2 = fplSettings.FPL_LEVEL_2_MAX / 100;
        const multiplier3 = fplSettings.FPL_LEVEL_3_MAX / 100;
        if (eligibleIncome != null) {
            const incomeLevel1 = (eligibleIncome * multiplier1).toLocaleString();
            const incomeLevel2 = (eligibleIncome * multiplier2).toLocaleString();
            const incomeLevel3 = (eligibleIncome * multiplier3).toLocaleString();

            switch (fpl) {
                case INCOME_RANGE.FPL_LVL_1_OR_LESS:
                    incomeRange = `$${incomeLevel1} or less`;
                    break;
                case INCOME_RANGE.FPL_LVL_1_TO_LVL_2:
                    incomeRange = `$${incomeLevel1} - $${incomeLevel2}`;
                    break;
                case INCOME_RANGE.FPL_LVL_2_TO_LVL_3:
                    incomeRange = `$${incomeLevel2} - $${incomeLevel3}`;
                    break;
                case INCOME_RANGE.FPL_LVL_3_PLUS:
                    incomeRange = `$${incomeLevel3} or more`;
                    break;
                case INCOME_RANGE.FPL_LVL_1_TO_LVL_3:
                    incomeRange = `$${incomeLevel1} - $${incomeLevel3}`;
                    break;
            }
        }

        return incomeRange;
    }

    private IncomeRangePercent(fpl: INCOME_RANGE, fplSettings: FplSettings): string {
        let incomeRangePercent = '';
        const level1 = fplSettings.FPL_LEVEL_1_MAX + "";
        const level2 = fplSettings.FPL_LEVEL_2_MAX + "";
        const level3 = fplSettings.FPL_LEVEL_3_MAX + "";

        switch (fpl) {
            case INCOME_RANGE.FPL_LVL_1_OR_LESS:
                incomeRangePercent = `${level1}% or less`
                break;
            case INCOME_RANGE.FPL_LVL_1_TO_LVL_2:
                incomeRangePercent = `${level1}% to ${level2}%`
                break;
            case INCOME_RANGE.FPL_LVL_2_TO_LVL_3:
                incomeRangePercent = `${level2}% to ${level3}%`
                break;
            case INCOME_RANGE.FPL_LVL_3_PLUS:
                incomeRangePercent = `${level3}% or more`
                break;
            case INCOME_RANGE.FPL_LVL_1_TO_LVL_3:
                incomeRangePercent = `${level1}% to ${level3}%`
                break;
        }

        return incomeRangePercent;
    }

    async NonIncomeFactors(appData: AppData): Promise<string> {
        const reasons = Array<string>();
        const gsrpContent = await this.contentService.getAlternateGSRPQualifier();
        const gsrpContent2 = await this.contentService.getAlternateGSRPQualifierList2();
        const hsContent = await this.contentService.getAlternateHSQualifierContent();
        const followup211Content = await this.contentService.getFollowup211Content();

        if (appData.foster === true) {
            reasons.push(hsContent.Foster)
        }
        if (appData.homeless === true) {
            reasons.push(hsContent.Homeless)
        }
        if (appData.benefits === true) {
            reasons.push(hsContent.Benefits)
        }
        if (appData.specialNeeds === true) {
            reasons.push(hsContent.SpecialNeeds)
        }
        if ( appData.migrantFarmWork === true) {
            reasons.push(followup211Content.MigrantFarmWork)
        }
        if (appData.noHighSchoolGraduation === true) {
            reasons.push(gsrpContent.NoGraduation)
        }
        if (appData.illiterate === true) {
            reasons.push(gsrpContent.Illiterate)
        }
        if (appData.noEnglishHousehold === true) {
            reasons.push(gsrpContent.NoEnglish)
        }
        if (appData.parentExperiencedAbuse === true) {
            reasons.push(gsrpContent.Abuse)
        }
        if (appData.disability === true) {
            reasons.push(gsrpContent2.Disability)
        }
        if (appData.expelled === true) {
            reasons.push(gsrpContent2.Expelled)
        }
        if (appData.englishSecond === true) {
            reasons.push(gsrpContent2.EnglishSecond)
        }
        if (appData.noEnglishHome === true) {
            reasons.push(gsrpContent2.NoEnglishHome)
        }
        if (appData.parentalLoss === true) {
            reasons.push(gsrpContent2.ParentalLoss)
        }
        if (appData.toxicExposure === true) {
            reasons.push(gsrpContent2.ToxicExposure)
        }
        if (appData.sexualAbuse === true) {
            reasons.push(gsrpContent2.SexualAbuse)
        }
        if (appData.neglect === true) {
            reasons.push(gsrpContent2.Neglect)
        }
        if (appData.protectiveServices === true) {
            reasons.push(gsrpContent2.ProtectiveServicesReport)
        }
        if (appData.under20Birth === true) {
            reasons.push(gsrpContent2.Under20Birth)
        }
        if (appData.highRiskNeighborhood === true) {
            reasons.push(gsrpContent2.HighRiskNeighborhood)
        }
        if (appData.siblingIssue === true) {
            reasons.push(gsrpContent2.SiblingIssue)
        }
        if (appData.headStartUnableToFind === true) {
            reasons.push("Recently applied to Head Start and not able to find a spot")
        } else if (appData.headStartUnableToFind === false) {
            reasons.push("Has not recently applied to Head Start")
        }

        return reasons.join(", ");
    }

}